import { useState, useEffect, useCallback } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import type {
  JobControlStatus,
  JobActivityResponse,
  JobUnitItem,
  DispatchStatusResponse,
  DispatchAuditLogResponse,
  DispatchChannelStatus,
} from "@shared-core/data/adapter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Button } from "../app/components/primitives/button";
import { Badge } from "../app/components/primitives/badge";
import { ListPagination } from "../app/components/patterns/ListPagination";
import {
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { currentTime } from "@shared-core/domain/current-time";

// --- Shared helpers ---

function StatusDot({ alive }: { alive: boolean }) {
  return alive ? (
    <CheckCircle className="h-4 w-4 text-green-500 inline-block" />
  ) : (
    <XCircle className="h-4 w-4 text-red-500 inline-block" />
  );
}

function timeAgo(iso: string | null): string {
  if (!iso) return "--";
  const diff = currentTime().getTime() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function elapsedSeconds(iso: string | null): string {
  if (!iso) return "";
  const diff = currentTime().getTime() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  return `${mins}m ${secs % 60}s`;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "--";
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}m ${secs}s`;
}

// --- Agent Dispatch Tab ---

const CHANNEL_LABELS: Record<string, string> = {
  issues: "Issues",
  pr_reviews: "PR Reviews",
  pr_fixers: "PR Fixers",
  investigations: "Investigations",
};

const DECISION_BADGE: Record<string, { className: string; label: string }> = {
  dispatched: {
    className: "text-green-600 border-green-300",
    label: "dispatched",
  },
  skipped: {
    className: "text-muted-foreground border-muted",
    label: "skipped",
  },
  paused: { className: "text-amber-600 border-amber-300", label: "paused" },
};

function AgentDispatchTab() {
  const dataAdapter = useDataAdapter();
  const { setVisibilityAwareInterval } = usePlatformCapabilities();
  const [dispatchStatus, setDispatchStatus] =
    useState<DispatchStatusResponse | null>(null);
  const [auditLog, setAuditLog] = useState<DispatchAuditLogResponse | null>(
    null,
  );
  const [auditPage, setAuditPage] = useState(1);
  const [channelFilter, setChannelFilter] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const refresh = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const [statusData, logData] = await Promise.all([
          dataAdapter.getDispatchStatus(),
          dataAdapter.getDispatchAuditLog({
            page: auditPage,
            channel: channelFilter || undefined,
            decision: decisionFilter || undefined,
          }),
        ]);
        setDispatchStatus(statusData);
        setAuditLog(logData);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [dataAdapter, auditPage, channelFilter, decisionFilter],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    return setVisibilityAwareInterval(() => refresh(true), 5_000);
  }, [refresh, setVisibilityAwareInterval]);

  const handlePauseChannel = async (channel: string) => {
    setActionLoading(channel);
    try {
      await dataAdapter.pauseDispatchChannel(channel);
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeChannel = async (channel: string) => {
    setActionLoading(channel);
    try {
      await dataAdapter.resumeDispatchChannel(channel);
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };

  const handlePauseAll = async () => {
    setActionLoading("all");
    try {
      await dataAdapter.pauseAllDispatchChannels();
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeAll = async () => {
    setActionLoading("all");
    try {
      await dataAdapter.resumeAllDispatchChannels();
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };

  if (loading && !dispatchStatus) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!dispatchStatus) return null;

  const anyPaused = dispatchStatus.channels.some(
    (c: DispatchChannelStatus) => c.paused,
  );
  const allPaused = dispatchStatus.channels.every(
    (c: DispatchChannelStatus) => c.paused,
  );

  return (
    <div className="space-y-6">
      {/* Env var override warning */}
      {dispatchStatus.env_override_active && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 inline-block mr-1" />
          AGENT_DISPATCH_PAUSED=1 is set — all channels are overridden as paused
          regardless of individual state.
        </div>
      )}

      {/* Channels */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Channels</h3>
          <div className="flex gap-2">
            {!allPaused && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePauseAll}
                disabled={actionLoading === "all"}
              >
                <Pause className="h-3 w-3 mr-1" /> Pause All
              </Button>
            )}
            {anyPaused && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResumeAll}
                disabled={actionLoading === "all"}
              >
                <Play className="h-3 w-3 mr-1" /> Resume All
              </Button>
            )}
          </div>
        </div>
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Channel</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Running</TableHead>
                <TableHead>Last Dispatch</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dispatchStatus.channels.map((ch: DispatchChannelStatus) => (
                <TableRow key={ch.channel}>
                  <TableCell className="font-medium">
                    {CHANNEL_LABELS[ch.channel] ?? ch.channel}
                  </TableCell>
                  <TableCell>
                    {ch.paused ? (
                      <span>
                        <Badge
                          variant="outline"
                          className="text-amber-600 border-amber-300"
                        >
                          paused
                        </Badge>
                        {ch.paused_by && (
                          <span className="text-xs text-muted-foreground ml-2">
                            by {ch.paused_by} {timeAgo(ch.paused_at)}
                          </span>
                        )}
                      </span>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-green-600 border-green-300"
                      >
                        enabled
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {ch.running_count > 0 ? (
                      <Badge
                        variant="outline"
                        className="text-blue-600 border-blue-300"
                      >
                        {ch.running_count} task{ch.running_count > 1 ? "s" : ""}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {timeAgo(ch.last_dispatch_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    {ch.paused ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResumeChannel(ch.channel)}
                        disabled={actionLoading === ch.channel}
                      >
                        <Play className="h-3 w-3 mr-1" /> Resume
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePauseChannel(ch.channel)}
                        disabled={actionLoading === ch.channel}
                      >
                        <Pause className="h-3 w-3 mr-1" /> Pause
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Running Tasks */}
      {dispatchStatus.running_tasks.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Running Tasks</h3>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Started</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dispatchStatus.running_tasks.map((task) => (
                  <TableRow key={task.task_arn}>
                    <TableCell className="text-sm">
                      {CHANNEL_LABELS[task.channel] ?? task.channel}
                    </TableCell>
                    <TableCell className="font-medium">
                      {task.channel === "issues"
                        ? `#${task.target_id}`
                        : task.channel.startsWith("pr")
                          ? `PR #${task.target_id}`
                          : task.target_id}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDuration(task.duration_seconds)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {timeAgo(task.started_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Dispatch Log */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Dispatch Log</h3>
        <div className="flex gap-2 mb-3">
          <select
            className="text-sm border rounded px-2 py-1 bg-background"
            value={channelFilter}
            onChange={(e) => {
              setChannelFilter(e.target.value);
              setAuditPage(1);
            }}
          >
            <option value="">All channels</option>
            {Object.entries(CHANNEL_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <select
            className="text-sm border rounded px-2 py-1 bg-background"
            value={decisionFilter}
            onChange={(e) => {
              setDecisionFilter(e.target.value);
              setAuditPage(1);
            }}
          >
            <option value="">All decisions</option>
            <option value="dispatched">Dispatched</option>
            <option value="skipped">Skipped</option>
            <option value="paused">Paused</option>
          </select>
        </div>
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Decision</TableHead>
                <TableHead>Outcome</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditLog?.items.map((event) => {
                const badge = DECISION_BADGE[event.decision] ?? {
                  className: "",
                  label: event.decision,
                };
                return (
                  <TableRow key={event.id}>
                    <TableCell className="text-sm text-muted-foreground">
                      {timeAgo(event.created_at)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {CHANNEL_LABELS[event.channel] ?? event.channel}
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="font-medium">
                        {event.target_type === "issue"
                          ? `#${event.target_id}`
                          : event.target_type === "pr"
                            ? `PR #${event.target_id}`
                            : event.target_id}
                      </span>
                      {event.target_title && (
                        <span className="text-muted-foreground ml-1">
                          {event.target_title}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={badge.className}>
                        {badge.label}
                      </Badge>
                      {event.decision !== "dispatched" && event.reason && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({event.reason})
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {event.exit_code !== null ? (
                        <span className="text-sm">
                          {event.exit_code === 0 ? (
                            <Badge
                              variant="outline"
                              className="text-green-600 border-green-300"
                            >
                              ok
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              exit {event.exit_code}
                            </Badge>
                          )}
                          {event.duration_seconds !== null && (
                            <span className="text-xs text-muted-foreground ml-1">
                              {formatDuration(event.duration_seconds)}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">--</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {auditLog && auditLog.items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-muted-foreground py-6"
                  >
                    No dispatch events
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {auditLog && auditLog.total_pages > 1 && (
          <ListPagination
            page={auditLog.page}
            totalPages={auditLog.total_pages}
            onPageChange={setAuditPage}
          />
        )}
      </div>
    </div>
  );
}

// --- Scheduled Jobs Tab (extracted from original page) ---

function Sparkline({ results }: { results: { status: string }[] }) {
  return (
    <span className="inline-flex gap-0.5">
      {results.map((r, i) => (
        <span
          key={i}
          className={`inline-block w-2 h-4 rounded-sm ${
            r.status === "done"
              ? "bg-green-500"
              : r.status === "skipped"
                ? "bg-gray-400"
                : "bg-red-500"
          }`}
          title={r.status}
        />
      ))}
    </span>
  );
}

function TimerStateBadge({
  state,
  hasDrift,
}: {
  state: string;
  hasDrift?: boolean;
}) {
  const badge =
    state === "paused" ? (
      <Badge variant="outline" className="text-amber-600 border-amber-300">
        paused
      </Badge>
    ) : (
      <Badge variant="outline" className="text-green-600 border-green-300">
        enabled
      </Badge>
    );
  if (!hasDrift) return badge;
  return (
    <span className="inline-flex items-center gap-1">
      {badge}
      <AlertTriangle
        className="h-3 w-3 text-amber-600"
        aria-label="Live state differs"
      />
    </span>
  );
}

function DriftCard({
  job,
  disabled,
  onAcceptLive,
  onReapplyDesired,
}: {
  job: JobUnitItem;
  disabled: boolean;
  onAcceptLive: () => void;
  onReapplyDesired: () => void;
}) {
  const shown = job.desired_timer_state;
  const live = job.systemd_live_state ?? "unknown";
  const keepLiveLabel = live === "paused" ? "Keep paused" : "Keep enabled";
  const reapplyLabel = shown === "paused" ? "Pause again" : "Re-enable";
  return (
    <div className="rounded-md border bg-background p-3 text-sm">
      <div className="font-medium">{job.display_name}</div>
      <div className="text-muted-foreground mb-2">
        shown as <strong>{shown}</strong>, but is actually{" "}
        <strong>{live}</strong>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || job.systemd_live_state === null}
          onClick={onAcceptLive}
          title="Update the UI to match the live state"
        >
          {keepLiveLabel}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={onReapplyDesired}
          title="Force the live state back to what the UI says"
        >
          {reapplyLabel}
        </Button>
        <span
          className="ml-auto text-xs text-muted-foreground"
          title="Someone likely changed this job's state outside the UI (e.g. on the VM via systemctl). Pick whichever state you want to keep."
        >
          What happened?
        </span>
      </div>
    </div>
  );
}

function LastResultBadge({
  result,
}: {
  result: { status: string; exit_code: number | null } | null;
}) {
  if (!result) return <span className="text-muted-foreground">--</span>;
  if (result.status === "done" && result.exit_code === 0) {
    return (
      <Badge variant="outline" className="text-green-600 border-green-300">
        ok
      </Badge>
    );
  }
  if (result.status === "skipped") {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        skipped
      </Badge>
    );
  }
  return <Badge variant="destructive">failed</Badge>;
}

function timeUntil(iso: string | null): string {
  if (!iso) return "--";
  const diff = new Date(iso).getTime() - currentTime().getTime();
  if (diff <= 0) return "now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.floor(mins / 60);
  return `in ${hours}h`;
}

function formatRowsSeen(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
}

function CurrentStepDetail({ job }: { job: JobUnitItem }) {
  const step = job.current_step;
  const seen = job.current_step_rows_seen;
  if (!step || seen === null) return null;
  const target = job.current_step_rows_target;
  const rowsLabel = target
    ? `${formatRowsSeen(seen)}/${formatRowsSeen(target)}`
    : formatRowsSeen(seen);
  return (
    <span className="block text-[11px] text-muted-foreground mt-0.5">
      {step} · {rowsLabel} rows
    </span>
  );
}

function CurrentStateBadge({ job }: { job: JobUnitItem }) {
  if (job.is_scheduled && !job.systemd_observed_at) {
    return (
      <span className="text-xs text-muted-foreground italic">capturing...</span>
    );
  }
  // For templated units (max_concurrency > 1) the cap-aware label
  // surfaces queue state — "running 2/3" or "queued, waiting for slot".
  // For single-instance units, the existing "running" / "idle" labels
  // continue to apply.
  const showsConcurrency = job.max_concurrency > 1;
  if (showsConcurrency && job.active_run_count > 0) {
    return (
      <span>
        <Badge variant="outline" className="text-blue-600 border-blue-300">
          running {job.active_run_count}/{job.max_concurrency}
        </Badge>
        <CurrentStepDetail job={job} />
      </span>
    );
  }
  if (job.is_running) {
    return (
      <span>
        <Badge variant="outline" className="text-blue-600 border-blue-300">
          running {elapsedSeconds(job.current_run_started_at)}
        </Badge>
        <CurrentStepDetail job={job} />
      </span>
    );
  }
  return <span className="text-sm text-muted-foreground">idle</span>;
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  unit_paused: "paused",
  unit_resumed: "resumed",
  run_requested: "run requested",
  run_started: "started",
  run_completed: "completed",
  run_failed: "failed",
  run_skipped: "skipped",
  stop_requested: "stop requested",
  unit_stop_issued: "stop issued",
  unit_stop_skipped: "stop skipped (not running)",
  unit_stop_failed: "stop failed",
  live_state_accepted: "accepted live state",
  desired_state_reapply_requested: "re-apply requested",
  systemd_state_captured: "captured live state",
  desired_state_repaired: "desired state repaired",
};

function ActorLabel({ actor }: { actor: string }) {
  if (actor === "schedule")
    return <span className="text-muted-foreground">scheduled</span>;
  if (actor === "external")
    return (
      <span
        className="text-muted-foreground"
        title="triggered outside the admin UI"
      >
        manual (ssh)
      </span>
    );
  return <span>{actor}</span>;
}

function ScheduledJobsTab() {
  const dataAdapter = useDataAdapter();
  const { setVisibilityAwareInterval } = usePlatformCapabilities();
  const [status, setStatus] = useState<JobControlStatus | null>(null);
  const [activity, setActivity] = useState<JobActivityResponse | null>(null);
  const [activityPage, setActivityPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const refresh = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const [jobsData, activityData] = await Promise.all([
          dataAdapter.getJobs(),
          dataAdapter.getJobActivity({ page: activityPage }),
        ]);
        setStatus(jobsData);
        setActivity(activityData);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [dataAdapter, activityPage],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    return setVisibilityAwareInterval(() => refresh(true), 5_000);
  }, [refresh, setVisibilityAwareInterval]);

  const handlePause = async (unitName: string) => {
    setActionLoading(unitName);
    try {
      await dataAdapter.pauseJob(unitName);
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };
  const handleResume = async (unitName: string) => {
    setActionLoading(unitName);
    try {
      await dataAdapter.resumeJob(unitName);
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };
  const handleRunNow = async (unitName: string) => {
    setActionLoading(unitName);
    try {
      await dataAdapter.runJobNow(unitName);
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };
  const handleAcceptLive = async (unitName: string) => {
    setActionLoading(unitName);
    try {
      await dataAdapter.acceptLiveState(unitName);
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };
  const handleReapplyDesired = async (unitName: string) => {
    setActionLoading(unitName);
    try {
      await dataAdapter.reapplyDesiredState(unitName);
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!status) return null;

  const orderedJobs = [
    ...status.jobs.filter((j) => j.is_scheduled),
    ...status.jobs.filter((j) => !j.is_scheduled),
  ];
  const pausedJobs = status.jobs.filter(
    (j) => j.desired_timer_state === "paused",
  );
  const driftedJobs = status.jobs.filter((j) => j.has_drift);
  // A skipped run (overlap guard fired) is a clean no-op, not a failure —
  // exclude it from the recent-failures banner so greys don't read as red.
  const failedCount = status.jobs.filter(
    (j) =>
      j.last_result &&
      j.last_result.status !== "done" &&
      j.last_result.status !== "skipped",
  ).length;

  return (
    <div className="space-y-6">
      {/* Warnings */}
      {(pausedJobs.length > 0 || failedCount > 0 || driftedJobs.length > 0) && (
        <div className="rounded-lg border bg-card p-4 space-y-2">
          {pausedJobs.map((job) => (
            <div
              key={job.unit_name}
              className="flex items-center gap-2 text-amber-600 text-sm"
            >
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                {job.display_name} paused
                {job.updated_by && job.updated_at && (
                  <span className="text-muted-foreground">
                    {" "}
                    since {timeAgo(job.updated_at)} by {job.updated_by}
                  </span>
                )}
              </span>
            </div>
          ))}
          {failedCount > 0 && (
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <XCircle className="h-4 w-4" />
              {failedCount} recent failure{failedCount > 1 ? "s" : ""}
            </div>
          )}
          {driftedJobs.length > 0 && (
            <div className="space-y-2 pt-2 border-t">
              <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                {driftedJobs.length === 1
                  ? "1 job is in a different state than expected"
                  : `${driftedJobs.length} jobs are in a different state than expected`}
              </div>
              {driftedJobs.map((job) => (
                <DriftCard
                  key={job.unit_name}
                  job={job}
                  disabled={actionLoading === job.unit_name}
                  onAcceptLive={() => handleAcceptLive(job.unit_name)}
                  onReapplyDesired={() => handleReapplyDesired(job.unit_name)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Jobs table */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Schedule</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Current</TableHead>
              <TableHead>Next</TableHead>
              <TableHead>Last Run</TableHead>
              <TableHead>Last 10</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orderedJobs.map((job) => {
              const isManual = !job.is_scheduled;
              return (
                <TableRow key={job.unit_name}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <span>{job.display_name}</span>
                      {job.warning_text && (
                        <span title={job.warning_text} className="inline-flex">
                          <AlertTriangle
                            className="h-3 w-3 text-amber-600 flex-shrink-0"
                            aria-label={job.warning_text}
                          />
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {isManual
                      ? (job.schedule_description ?? "On demand")
                      : (job.schedule_description ?? "\u2014")}
                  </TableCell>
                  <TableCell>
                    {isManual ? (
                      <span className="text-muted-foreground">\u2014</span>
                    ) : (
                      <TimerStateBadge
                        state={job.desired_timer_state}
                        hasDrift={job.has_drift}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <CurrentStateBadge job={job} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {isManual || job.desired_timer_state === "paused"
                      ? "--"
                      : timeUntil(job.next_run_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <LastResultBadge result={job.last_result} />
                      <span className="text-xs text-muted-foreground">
                        {timeAgo(job.last_result?.completed_at ?? null)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Sparkline results={job.recent_results} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {!isManual &&
                        (job.desired_timer_state === "enabled" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePause(job.unit_name)}
                            disabled={actionLoading === job.unit_name}
                          >
                            <Pause className="h-3 w-3 mr-1" />
                            Pause
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResume(job.unit_name)}
                            disabled={actionLoading === job.unit_name}
                          >
                            <Play className="h-3 w-3 mr-1" />
                            Resume
                          </Button>
                        ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRunNow(job.unit_name)}
                        disabled={
                          actionLoading === job.unit_name ||
                          job.active_run_count >= job.max_concurrency
                        }
                        title={
                          job.active_run_count >= job.max_concurrency
                            ? `At capacity (${job.active_run_count}/${job.max_concurrency})`
                            : "Run now"
                        }
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Run now
                      </Button>
                      {job.grafana_logs_url && (
                        <a
                          href={job.grafana_logs_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Logs
                        </a>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Activity feed */}
      {activity && activity.items.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Recent Activity</h3>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activity.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-sm text-muted-foreground">
                      {timeAgo(item.occurred_at)}
                    </TableCell>
                    <TableCell className="text-sm">
                      <ActorLabel actor={item.actor} />
                    </TableCell>
                    <TableCell className="text-sm">
                      {EVENT_TYPE_LABELS[item.event_type] ?? item.event_type}
                    </TableCell>
                    <TableCell className="text-sm">{item.unit_name}</TableCell>
                    <TableCell>
                      {item.event_type === "run_completed" ? (
                        <Badge
                          variant="outline"
                          className="text-green-600 border-green-300"
                        >
                          ok
                        </Badge>
                      ) : item.event_type === "run_failed" ? (
                        <Badge variant="destructive">failed</Badge>
                      ) : item.event_type === "run_skipped" ? (
                        <Badge
                          variant="outline"
                          className="text-muted-foreground"
                        >
                          skipped
                        </Badge>
                      ) : (
                        <Badge variant="outline">
                          {EVENT_TYPE_LABELS[item.event_type] ??
                            item.event_type}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ListPagination
            page={activity.page}
            totalPages={activity.total_pages}
            onPageChange={setActivityPage}
          />
        </div>
      )}
    </div>
  );
}

// --- Main page with tabs ---

export function SettingsJobsPage() {
  const [activeTab, setActiveTab] = useState<"dispatch" | "scheduled">(
    "dispatch",
  );
  const dataAdapter = useDataAdapter();
  const { setVisibilityAwareInterval } = usePlatformCapabilities();
  const [agentAlive, setAgentAlive] = useState(false);

  // Poll agent health (shared across tabs)
  const refreshAgentHealth = useCallback(async () => {
    try {
      const jobs = await dataAdapter.getJobs();
      setAgentAlive(jobs.agent_alive);
    } catch {
      /* ignore */
    }
  }, [dataAdapter]);

  useEffect(() => {
    refreshAgentHealth();
  }, [refreshAgentHealth]);
  useEffect(() => {
    return setVisibilityAwareInterval(refreshAgentHealth, 15_000);
  }, [refreshAgentHealth, setVisibilityAwareInterval]);

  return (
    <div className="space-y-6">
      {/* Agent health strip */}
      <div className="rounded-lg border bg-card p-4">
        <span className="text-sm text-muted-foreground">
          Agent: <StatusDot alive={agentAlive} />{" "}
          {agentAlive ? "healthy" : "offline"}
        </span>
      </div>

      {/* Tab bar */}
      <div className="border-b">
        <div className="flex gap-0">
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "dispatch"
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setActiveTab("dispatch")}
          >
            Agent Dispatch
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "scheduled"
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setActiveTab("scheduled")}
          >
            Scheduled Jobs
          </button>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === "dispatch" ? <AgentDispatchTab /> : <ScheduledJobsTab />}
    </div>
  );
}
